import { MUSIC_FADE } from '../config/audio-tuning';

/**
 * Looping music bed for the non-combat screens.
 *
 * Uses a decoded `AudioBuffer` with `loop = true` rather than an
 * `<audio loop>` element: the element re-primes its decoder at the loop point
 * in several browsers and drops a short gap there, which would throw away the
 * seamless loop the tracks are mastered for (`tools/master-music.sh`). A buffer
 * source loops sample-accurately, so the join is exactly the crossfade that was
 * baked in.
 *
 * The cost of that choice is memory and an up-front decode: a 96kbps 85s track
 * is ~1MB over the wire and ~15MB decoded. That is why `play()` fetches lazily
 * and why a muted player never fetches at all — on a phone, a bed nobody is
 * going to hear should not cost a megabyte of a metered connection.
 *
 * Every method is a no-op without Web Audio (jsdom, and any browser that
 * refuses us a context), matching `AudioEngine`'s degrade-quietly contract.
 */
export class MusicPlayer {
  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  /** In flight or resolved, keyed by url — so a re-entrant play() never double-fetches a megabyte. */
  private loading: Promise<AudioBuffer | null> | null = null;
  private loadedUrl: string | null = null;
  private currentUrl: string | null = null;
  private disposed = false;

  constructor(
    private readonly context: AudioContext,
    private readonly destination: AudioNode,
  ) {}

  /** True while a track is mounted and audible-in-principle (the bus gain is a separate question). */
  get isPlaying(): boolean {
    return this.source !== null;
  }

  /** The track currently mounted, or null. Lets a caller skip a redundant restart. */
  get playingUrl(): string | null {
    return this.currentUrl;
  }

  /**
   * Fetch (once), decode (once) and start looping `url`, fading in.
   * Calling it with the track already playing is a no-op, so a screen may call
   * this on every state refresh without restarting the music under the player.
   */
  async play(url: string): Promise<void> {
    if (this.disposed) return;
    if (this.currentUrl === url && this.source) return;

    const buffer = await this.load(url);
    // `load` awaits the network: the screen may have been torn down, or another
    // track requested, while we were out there. Either makes this start stale.
    if (!buffer || this.disposed || (this.currentUrl !== null && this.currentUrl !== url)) return;

    this.stopImmediate();
    this.currentUrl = url;

    const gain = this.context.createGain();
    gain.gain.value = 0;
    gain.connect(this.destination);

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start();

    this.gain = gain;
    this.source = source;

    const now = this.context.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + MUSIC_FADE.IN_S);
  }

  /**
   * Fade out and stop. Resolves once the node is actually torn down, so a
   * caller that immediately starts a different track can await it and avoid
   * two beds overlapping.
   */
  stop(): Promise<void> {
    const source = this.source;
    const gain = this.gain;
    this.source = null;
    this.gain = null;
    this.currentUrl = null;
    if (!source || !gain) return Promise.resolve();

    const now = this.context.currentTime;
    // cancel + re-anchor at the live value, or an in-flight fade-in would keep
    // ramping up underneath the fade-out.
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + MUSIC_FADE.OUT_S);

    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          source.stop();
        } catch {
          // Already stopped — a double stop() throws in some engines and means nothing here.
        }
        source.disconnect();
        gain.disconnect();
        resolve();
      }, MUSIC_FADE.OUT_S * 1000);
    });
  }

  dispose(): void {
    this.disposed = true;
    this.stopImmediate();
    this.buffer = null;
    this.loading = null;
    this.loadedUrl = null;
  }

  private stopImmediate(): void {
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        // See stop() — a redundant stop is not an error worth surfacing.
      }
      this.source.disconnect();
      this.source = null;
    }
    if (this.gain) {
      this.gain.disconnect();
      this.gain = null;
    }
    this.currentUrl = null;
  }

  private load(url: string): Promise<AudioBuffer | null> {
    if (this.buffer && this.loadedUrl === url) return Promise.resolve(this.buffer);
    if (this.loading && this.loadedUrl === url) return this.loading;

    this.loadedUrl = url;
    this.loading = (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const bytes = await response.arrayBuffer();
        const decoded = await this.context.decodeAudioData(bytes);
        this.buffer = decoded;
        return decoded;
      } catch {
        // A missing or undecodable bed is a silent screen, never a broken one.
        this.loadedUrl = null;
        this.loading = null;
        return null;
      }
    })();
    return this.loading;
  }
}
